import { FC, useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslate } from 'hooks/useTranslate';
import { useDispatch } from 'react-redux';
import { store } from 'redux/store';

import { BoardVideo } from 'types';
import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBoard } from 'modules/boards/redux/boardActions';
import successSwal from 'utils/successSwal';
import errorSwal from 'utils/errorSwal';

type RenameVideoModalProps = {
  boardId: string;
  currentVideos: BoardVideo[];
  videoIndex: number;
};

const RenameVideoModal: FC<RenameVideoModalProps> = ({
  boardId,
  currentVideos,
  videoIndex,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const video = currentVideos[videoIndex];
  const [title, setTitle] = useState(video?.title || '');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (video) {
      setTitle(video.title);
    }
  }, [video]);

  const handleClose = () => {
    dispatch(setModal(null));
  };

  const handleSave = async () => {
    if (!title.trim()) {
      errorSwal(t('TitleRequired') || 'Title is required');
      return;
    }

    try {
      setIsLoading(true);

      const updatedVideos = [...currentVideos];
      updatedVideos[videoIndex] = {
        ...updatedVideos[videoIndex],
        title: title.trim(),
      };

      await store.dispatch(
        updateBoard(boardId, { videos: updatedVideos }, false)
      );

      successSwal(t('VideoRenamedSuccessfully') || 'Video renamed successfully');
      handleClose();
    } catch (error) {
      errorSwal(t('ErrorRenamingVideo') || 'Error renaming video');
    } finally {
      setIsLoading(false);
    }
  };

  if (!video) {
    return null;
  }

  return (
    <>
      <Modal.Header>
        <h5 className="modal-title">{t('RenameVideo') || 'Rename Video'}</h5>
        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body>
        <form
          className="form-validate is-alter"
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
        >
          <div className="form-group">
            <label className="form-label" htmlFor="video-title">
              {t('Title') || 'Title'}
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                id="video-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('EnterVideoTitle') || 'Enter video title'}
                required
              />
            </div>
          </div>

          <div className="form-group mt-4">
            <button
              type="submit"
              className="btn btn-lg btn-primary"
              disabled={!title.trim() || isLoading}
            >
              {isLoading
                ? t('Saving') || 'Saving...'
                : t('Save') || 'Save'}
            </button>
            <button
              type="button"
              className="btn btn-lg btn-link ml-2"
              onClick={handleClose}
              disabled={isLoading}
            >
              {t('Cancel') || 'Cancel'}
            </button>
          </div>
        </form>
      </Modal.Body>
    </>
  );
};

export default RenameVideoModal;

